import { type ReactNode, useEffect, useSyncExternalStore } from 'react';
import { getColorScheme, setColorScheme, subscribeColorScheme } from './color-scheme';
import { applyTheme } from './theme';
import { toThemeName } from './tokens';

/** The backend `/theme` response: a flat map of token name to colour. */
export type ThemeData = Record<string, string>;

export interface ThemeProviderProps {
  /** Result of the app's theme query (e.g. RTK `useGetThemeQuery().data`). */
  data: ThemeData | undefined;
  children: ReactNode;
}

/**
 * Applies the backend `company-theme` to the document. Light/dark follows the OS via `color-scheme`
 * + `light-dark()` in the stylesheet. The query stays app-specific and is injected via `data`.
 */
export const ThemeProvider = ({ data, children }: ThemeProviderProps) => {
  useEffect(() => {
    if (!data) {
      return;
    }

    applyTheme(toThemeName(data['company-theme']));
  }, [data]);

  return children;
};

/**
 * Persisted colour-scheme state. Setting a scheme applies it to the document and persists it
 * (including `system`, which clears the root override so the OS preference applies). With no
 * stored value the scheme is `light`. Backed by a module-level store, so every instance across
 * the app re-renders on a change. See color-scheme.ts for the mechanism.
 */
export const useColorScheme = () => {
  const colorScheme = useSyncExternalStore(subscribeColorScheme, getColorScheme);
  return [colorScheme, setColorScheme] as const;
};
